import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getSimulation } from '../client';
import { useSimulationId } from './useSimulationId';

export type SimulationStatus =
  | { state: 'loading' }
  | {
      state: 'not-started' | 'running' | 'paused' | 'finished';
      label: string;
      iterationDurationS: number | null;
      pauseAtS: number | null;
      simulationTimeS: number;
      remainingS: number | null;
    };

export function useSimulationStatus(): SimulationStatus {
  const simId = useSimulationId();
  const [tick, setTick] = useState(0);
  const refetchedOnExpiryRef = useRef(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['simulation', simId],
    queryFn: () => getSimulation({ path: { sim_id: simId }, throwOnError: true }).then(({ data }) => data),
    refetchInterval: 30_000,
  });

  // Local tick at 200ms — only when running, zero overhead otherwise
  useEffect(() => {
    if (data?.state !== 'running') return;
    const id = setInterval(() => setTick((t) => t + 1), 200);
    return () => clearInterval(id);
  }, [data?.state]);

  // Reset expiry guard when server data changes
  useEffect(() => {
    refetchedOnExpiryRef.current = false;
  }, [data?.remaining_s, data?.simulation_time_s]);

  if (isLoading || !data) {
    return { state: 'loading' };
  }

  // Compute local elapsed time — smooth interpolation between server polls
  const localTimeS =
    data.state === 'running' && data.current_run_start_at
      ? data.cumulative_time_s + (Date.now() - Date.parse(data.current_run_start_at)) / 1000
      : data.simulation_time_s;

  // Compute local remaining — offset server value by local time drift
  let remainingS: number | null = null;
  if (data.remaining_s != null) {
    if (data.state === 'paused' && data.remaining_s <= 0) {
      // Iteration expired while paused — show as null (no countdown)
      remainingS = null;
    } else {
      const drift = localTimeS - data.simulation_time_s;
      remainingS = Math.max(0, data.remaining_s - drift);
    }
  }

  // Auto-refetch when countdown expires — the GET triggers backend auto-pause
  if (data.state === 'running' && remainingS !== null && remainingS <= 0 && !refetchedOnExpiryRef.current) {
    refetchedOnExpiryRef.current = true;
    refetch();
  }

  // tick is used only to drive re-renders at 200ms intervals
  void tick;

  return {
    state: data.state,
    label: data.label,
    iterationDurationS: data.iteration_duration_s ?? null,
    pauseAtS: data.pause_at_s ?? null,
    simulationTimeS: localTimeS,
    remainingS,
  };
}
